/**
 * Route Payment Helper
 * Utility functions to integrate Razorpay Route with existing payment flow
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const functions = getFunctions();

// Commission rates by payment type
export const COMMISSION_RATES = {
  application_fee: 10,      // 10% commission
  admission_fee: 10,        // 10% commission
  teacher_hiring: 10,       // 10% commission
  batch_enrollment: 0,      // 0% commission - full amount to institute
  course_enrollment: 0,     // 0% commission - full amount to institute
  tuition_fee: 0,           // 0% commission - full amount to institute
  fee_payment: 0,           // 0% commission - full amount to institute
  store_purchase: 0         // 0% commission - full amount to institute
};

// Get commission rate for payment type
export const getCommissionRate = (paymentType) => {
  return COMMISSION_RATES[paymentType] ?? 10;
};

// Legacy exports for backward compatibility
export const PLATFORM_FEE_PERCENTAGE = 10;
export const RECIPIENT_PERCENTAGE = 90;

/**
 * Payment types that should use Route (split payments)
 */
export const ROUTE_PAYMENT_TYPES = [
  'application_fee',
  'admission_fee', 
  'teacher_hiring',
  'batch_enrollment',
  'course_enrollment',
  'fee_payment',
  'store_purchase'
];

/**
 * Check if a payment type should use Razorpay Route
 * @param {string} paymentType - Type of payment
 * @param {string|null} coachingId - Coaching ID (null for admin-created content)
 * @returns {boolean}
 */
export const shouldUseRoute = (paymentType, coachingId = null) => {
  // Admin-created content (coachingId = null) doesn't use Route
  if (paymentType === 'batch_enrollment' || paymentType === 'course_enrollment') {
    return coachingId !== null && coachingId !== undefined;
  }
  
  return ROUTE_PAYMENT_TYPES.includes(paymentType);
};

/**
 * Check if recipient has a linked account for receiving payments
 * @param {string} recipientUserId - Recipient's user ID
 * @returns {Promise<{canReceive: boolean, linkedAccountId: string|null, message: string}>}
 */
export const checkRecipientPayoutStatus = async (recipientUserId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', recipientUserId));
    
    if (!userDoc.exists()) {
      return {
        canReceive: false,
        linkedAccountId: null,
        message: 'Recipient not found'
      };
    }
    
    const userData = userDoc.data();
    const linkedAccountId = userData.razorpayLinkedAccountId;
    const payoutEnabled = userData.payoutEnabled !== false;
    
    if (!linkedAccountId) {
      return {
        canReceive: false,
        linkedAccountId: null,
        message: 'Recipient has not set up bank account for receiving payments'
      };
    }
    
    if (!payoutEnabled) {
      return {
        canReceive: false,
        linkedAccountId,
        message: 'Recipient\'s payout account is not active'
      };
    }
    
    return {
      canReceive: true,
      linkedAccountId,
      message: 'Recipient can receive payments'
    };
  } catch (error) {
    console.error('Error checking recipient payout status:', error);
    return {
      canReceive: false,
      linkedAccountId: null,
      message: 'Error checking recipient status'
    };
  }
};

/**
 * Create a Razorpay order with Route transfer
 * @param {Object} params - Order parameters
 * @returns {Promise<Object>} Order details with breakdown
 */
export const createRouteOrder = async ({
  amount,
  recipientUserId,
  paymentType,
  notes = {},
  receipt
}) => {
  try {
    // First check if recipient can receive payments
    const recipientStatus = await checkRecipientPayoutStatus(recipientUserId);
    
    if (!recipientStatus.canReceive) {
      throw new Error(recipientStatus.message);
    }
    
    // Create order with transfer via Firebase function
    const createOrderFn = httpsCallable(functions, 'createOrderWithTransfer');
    const result = await createOrderFn({
      amount,
      recipientUserId,
      paymentType,
      notes,
      receipt: receipt || `rcpt_${paymentType}_${Date.now()}`
    });
    
    return result.data;
  } catch (error) {
    console.error('Error creating Route order:', error);
    throw error;
  }
};

/**
 * Calculate payment breakdown for display
 * @param {number} amount - Total amount in rupees
 * @param {string} paymentType - Type of payment
 * @returns {Object} Breakdown details
 */
export const calculateBreakdown = (amount, paymentType = 'application_fee') => {
  const commissionRate = getCommissionRate(paymentType);
  const recipientPercentage = 100 - commissionRate;
  
  const platformFee = (amount * commissionRate) / 100;
  const recipientAmount = amount - platformFee;
  
  return {
    totalAmount: parseFloat(amount.toFixed(2)),
    platformFee: parseFloat(platformFee.toFixed(2)),
    platformFeePercentage: commissionRate,
    recipientAmount: parseFloat(recipientAmount.toFixed(2)),
    recipientPercentage: recipientPercentage
  };
};

/**
 * Get payment configuration based on payment type
 * @param {Object} paymentData - Payment data
 * @returns {Object} Payment configuration
 */
export const getPaymentConfig = (paymentData) => {
  const {
    paymentType,
    coachingId,
    recipientUserId,
    amount
  } = paymentData;
  
  const useRoute = shouldUseRoute(paymentType, coachingId);
  
  if (useRoute && recipientUserId) {
    const breakdown = calculateBreakdown(amount, paymentType);
    const commissionRate = getCommissionRate(paymentType);
    
    let description;
    if (commissionRate === 0) {
      description = `Full amount (₹${breakdown.recipientAmount}) will be transferred instantly to the recipient. No platform fee.`;
    } else {
      description = `${breakdown.recipientPercentage}% (₹${breakdown.recipientAmount}) will be transferred instantly to the recipient. Platform fee: ${commissionRate}%`;
    }
    
    return {
      useRoute: true,
      breakdown,
      recipientUserId,
      description
    };
  }
  
  return {
    useRoute: false,
    breakdown: null,
    recipientUserId: null,
    description: null
  };
};

/**
 * Initialize Razorpay checkout with Route order
 * This is a drop-in replacement for regular Razorpay checkout when Route is needed
 * @param {Object} options - Checkout options
 * @returns {Promise<void>}
 */
export const initializeRouteCheckout = async ({
  amount,
  recipientUserId,
  paymentType,
  payerDetails,
  notes = {},
  razorpayKeyId,
  onSuccess,
  onFailure,
  onDismiss
}) => {
  try {
    // Create order with transfer
    const orderResult = await createRouteOrder({
      amount,
      recipientUserId,
      paymentType,
      notes
    });
    
    if (!orderResult.success) {
      throw new Error(orderResult.error || 'Failed to create order');
    }
    
    const { order, breakdown, recipientInfo } = orderResult;
    
    // Use recipient's business name on the payment modal
    const displayName = recipientInfo?.businessName || 'MahStry';
    
    // Initialize Razorpay checkout
    const options = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      name: displayName,
      description: `Payment to ${displayName} for ${paymentType.replace(/_/g, ' ')}`,
      order_id: order.id,
      prefill: {
        name: payerDetails?.name || '',
        email: payerDetails?.email || '',
        contact: payerDetails?.phone || ''
      },
      notes: {
        ...notes,
        paymentType,
        recipientUserId,
        isRoutePayment: true
      },
      theme: {
        color: '#3B82F6'
      },
      handler: function(response) {
        if (onSuccess) {
          onSuccess({
            ...response,
            breakdown,
            orderId: order.id,
            isRoutePayment: true
          });
        }
      },
      modal: {
        ondismiss: function() {
          if (onDismiss) {
            onDismiss();
          } else if (onFailure) {
            onFailure({ error: 'Payment cancelled by user' });
          }
        }
      }
    };
    
    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      throw new Error('Razorpay SDK not loaded');
    }
    
    const razorpay = new window.Razorpay(options);
    
    razorpay.on('payment.failed', function(response) {
      if (onFailure) {
        onFailure({
          error: response.error.description,
          code: response.error.code,
          reason: response.error.reason
        });
      }
    });
    
    razorpay.open();
    
    return { success: true, orderId: order.id, breakdown };
    
  } catch (error) {
    console.error('Error initializing Route checkout:', error);
    if (onFailure) {
      onFailure({ error: error.message });
    }
    throw error;
  }
};

export default {
  shouldUseRoute,
  checkRecipientPayoutStatus,
  createRouteOrder,
  calculateBreakdown,
  getPaymentConfig,
  initializeRouteCheckout,
  getCommissionRate,
  COMMISSION_RATES,
  PLATFORM_FEE_PERCENTAGE,
  RECIPIENT_PERCENTAGE,
  ROUTE_PAYMENT_TYPES
};
